import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { CollectionSearchBar } from "../components/CollectionSearchBar";
import { CollectionFilters } from "../components/CollectionFilters";
import { ProblemCard } from "../components/ProblemCard";
import { Pagination } from "../components/Pagination";
import { collectionsApi } from "../services/collectionsApi";
import { apiService, Problem } from "../services/api";
import { Collection } from "../types/collection";
import { TrashIcon } from "../components/CustomIcons";
import { PinIcon } from "../components/PinIcon";
import { logger } from "../services/logger/BrowserLogger";
import ConfirmationModal from "../components/ConfirmationModal";
import LoadingModal from "../components/LoadingModal";
import { AddProblemsModal } from "../components/AddProblemsModal";
import GenerationProgress from "../components/GenerationProgress";
import { useAuth } from "../contexts/FirebaseAuthContext";
import { useUserSession } from "../hooks/useUserSession";
import { usePinManagement } from "../hooks/usePinManagement";
import { useWarmup } from "../hooks/useWarmup";
import { FloatingChatButton } from "../components/FloatingChatButton";
import { ActiveProblemProvider } from "../contexts/ActiveProblemContext";
import "../styles/CollectionPage.css";

// Difficulty order for default sorting
const DIFFICULTY_ORDER: Record<string, number> = {
  'Easy': 1,
  'Medium': 2,
  'Hard': 3
};

// Helper to sort problems by sequence or default (difficulty)
const sortBySequence = (problems: Problem[], sequence?: string[]): Problem[] => {
  if (!problems || problems.length === 0) return problems;

  if (sequence && sequence.length > 0) {
    // Sort by sequence order
    const sequenceMap = new Map(sequence.map((id, idx) => [id, idx]));
    return [...problems].sort((a, b) => {
      const aIdx = sequenceMap.get(a.id) ?? 999999;
      const bIdx = sequenceMap.get(b.id) ?? 999999;
      return aIdx - bIdx;
    });
  } else {
    // Default sort by difficulty (Easy -> Medium -> Hard)
    return [...problems].sort((a, b) => {
      return (DIFFICULTY_ORDER[a.difficulty] || 99) - (DIFFICULTY_ORDER[b.difficulty] || 99);
    });
  }
};

// Helper to convert Firestore timestamp to Date
// Firestore timestamps from API come as {_seconds: number, _nanoseconds: number}
const formatFirestoreDate = (timestamp: unknown): string => {
  if (!timestamp) return '—';

  // Handle Firestore timestamp object
  if (typeof timestamp === 'object' && timestamp !== null && '_seconds' in timestamp) {
    const ts = timestamp as { _seconds: number; _nanoseconds?: number };
    return new Date(ts._seconds * 1000).toLocaleDateString();
  }

  // Handle regular Date or date string
  const date = new Date(timestamp as string | number | Date);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString();
  }

  return '—';
};

export const CollectionPage: React.FC = () => {
  const { id, problemId } = useParams<{ id: string; problemId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const { clearSession } = useUserSession();
  const {
    pinProblem,
    unpinProblem,
    isProblemPinned,
    sortProblems,
    maxPinnedProblemsPerCollection,
    pinData
  } = usePinManagement();

  // Warm backend services for chat (AI assistant) when user views a collection
  useWarmup(['chat', 'problems']);

  // Get collection from navigation state if available
  const navigationCollection = location.state?.collection;
  
  // Collection data
  const [collection, setCollection] = useState<Collection | null>(navigationCollection || null);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [collectionProblems, setCollectionProblems] = useState<Problem[]>([]);
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  
  // UI state
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSearching, _setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAddProblemsModal, setShowAddProblemsModal] = useState(false);
  
  // Generation progress state
  const [showGenerationProgress, setShowGenerationProgress] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({
    totalCount: 0,
    currentBatch: 0,
    totalBatches: 0,
    generatedCount: 0,
    failedCount: 0,
    isComplete: false,
    message: ''
  });
  
  // Modal state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [problemToRemove, setProblemToRemove] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isAddingToCollection, _setIsAddingToCollection] = useState(false);

  // Mark all completed state
  const [showMarkAllModal, setShowMarkAllModal] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  // Filter state
  const [selectedDifficulty, setSelectedDifficulty] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedPattern, setSelectedPattern] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editedDesc, setEditedDesc] = useState("");
  
  // Generation tracking
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setGenerationStatus] = useState<Record<string, any>>({});


  // Scroll to specific problem if problemId is provided
  useEffect(() => {
    if (problemId && !loading && problems.length > 0) {
      // Find the problem index
      const problemIndex = problems.findIndex(p => p.id === problemId);
      if (problemIndex !== -1) {
        // Calculate which page this problem is on
        const problemPage = Math.floor(problemIndex / pageSize) + 1;
        if (problemPage !== currentPage) {
          setCurrentPage(problemPage);
        }
        
        // Scroll to the problem card after a short delay to allow rendering
        setTimeout(() => {
          const problemElement = document.getElementById(`problem-${problemId}`);
          if (problemElement) {
            problemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a temporary highlight effect
            problemElement.classList.add('problem-highlight');
            setTimeout(() => {
              problemElement.classList.remove('problem-highlight');
            }, 2000);
          }
        }, 100);
      }
    }
  }, [problemId, loading, problems, pageSize, currentPage]);

  // Removed placeholder checking since all placeholders have been deleted from the database
  // This was causing unnecessary individual API calls
  useEffect(() => {
    // Placeholder checking removed - no longer needed
  }, [collection, problems]);

  useEffect(() => {
    const fetchCollection = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // If we have the collection from navigation, use it
        if (navigationCollection) {
          logger.info('Using collection from navigation state');
          setEditedName(navigationCollection.name);
          setEditedDesc(navigationCollection.description);
          
          // Load all problems at once (using cache)
          if (navigationCollection.problemIds && navigationCollection.problemIds.length > 0) {
            logger.info(`Loading all ${navigationCollection.problemIds.length} problems from collection`);
            const allProblems = await apiService.getCollectionProblems(id, navigationCollection.problemIds, 1, -1);

            // Apply sequence order (or default difficulty sort)
            const sortedProblems = sortBySequence(allProblems, navigationCollection.problemSequence);
            setProblems(sortedProblems);
            setCollectionProblems(sortedProblems);
          } else {
            setProblems([]);
            setCollectionProblems([]);
          }

          // Fetch all completion statuses for the user
          if (userProfile) {
            try {
              const statuses = await apiService.getAllCompletionStatus();
              setCompletionStatus(statuses);
            } catch (error) {
              logger.warn('Failed to load completion statuses:', error);
              setCompletionStatus({});
            }
          }
        } else {
          // No navigation state - directly fetch the collection
          logger.info('No navigation state, fetching collection directly');
          
          try {
            // Directly try to get the collection (works for both user and system collections)
            const response = await collectionsApi.getCollection(id);
            logger.info('Collection loaded', { collection: response.collection });
            logger.info('Collection ownerId', { ownerId: response.collection.ownerId });
            
            setCollection(response.collection);
            setEditedName(response.collection.name);
            setEditedDesc(response.collection.description);
            
            // Load problems in parallel if there are any
            if (response.collection.problemIds && response.collection.problemIds.length > 0) {
              logger.info(`Loading all ${response.collection.problemIds.length} problems from collection`);
              const allProblems = await apiService.getCollectionProblems(id, response.collection.problemIds, 1, -1);

              logger.info(`Loaded ${allProblems.length} problems successfully`);
              // Apply sequence order (or default difficulty sort)
              const sortedProblems = sortBySequence(allProblems, response.collection.problemSequence);
              setProblems(sortedProblems);
              setCollectionProblems(sortedProblems);
            } else {
              logger.info('No problems in collection');
              setProblems([]);
              setCollectionProblems([]);
            }

            // Fetch all completion statuses for the user
            if (userProfile) {
              try {
                const statuses = await apiService.getAllCompletionStatus();
                setCompletionStatus(statuses);
              } catch (error) {
                logger.warn('Failed to load completion statuses:', error);
                setCompletionStatus({});
              }
            }
          } catch (error) {
            console.error('Failed to load collection:', error);
            // Don't throw - let the UI show "Collection not found" gracefully
            // If this was a restored session and collection doesn't exist, clear the session
            if (location.state?.restored) {
              await clearSession();
            }
          }
        }
      } catch (error) {
        console.error("Failed to load collection:", error);
        // Still set collection to null so the UI shows "Collection not found"
        setCollection(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigationCollection]);

  const loadCollection = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      // First, load the collection without problems
      const response = await collectionsApi.getCollection(id!);
      setCollection(response.collection);
      setEditedName(response.collection.name);
      setEditedDesc(response.collection.description);
      
      // Load all problems at once (using cache)
      if (response.collection.problemIds && response.collection.problemIds.length > 0) {
        console.log(`Loading all ${response.collection.problemIds.length} problems from collection`);
        const allProblems = await apiService.getCollectionProblems(id!, response.collection.problemIds, 1, -1);

        // Apply sequence order (or default difficulty sort)
        const sortedProblems = sortBySequence(allProblems, response.collection.problemSequence);
        setProblems(sortedProblems);
        setCollectionProblems(sortedProblems);
      } else {
        setProblems([]);
        setCollectionProblems([]);
      }

      // Fetch all completion statuses for the user
      if (userProfile) {
        try {
          const statuses = await apiService.getAllCompletionStatus();
          setCompletionStatus(statuses);
        } catch (error) {
          console.warn('Failed to load completion statuses:', error);
          setCompletionStatus({});
        }
      }
    } catch (error) {
      console.error('Failed to load collection:', error);
    } finally {
      if (showRefreshing) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);

    if (!query.trim()) {
      // Reset to original collection problems
      applyFilters(collectionProblems || []);
      return;
    }

    // Filter problems within collection
    const filtered = (collectionProblems || []).filter(p =>
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      p.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
    );
    applyFilters(filtered);
  };

  // Extract unique companies from all problems
  const getAvailableCompanies = (): string[] => {
    const companiesSet = new Set<string>();
    (collectionProblems || []).forEach(problem => {
      if (problem.companies && Array.isArray(problem.companies)) {
        problem.companies.forEach(company => {
          if (company.name) {
            companiesSet.add(company.name);
          }
        });
      }
    });
    return Array.from(companiesSet);
  };

  // Extract unique patterns from all problems
  const getAvailablePatterns = (): string[] => {
    const patternsSet = new Set<string>();
    (collectionProblems || []).forEach(problem => {
      if (problem.pattern && problem.pattern.name) {
        patternsSet.add(problem.pattern.name);
      }
    });
    return Array.from(patternsSet);
  };

  // Apply filters to problems
  const applyFilters = (problemsToFilter: Problem[]) => {
    let filtered = [...problemsToFilter];

    // Filter by difficulty
    if (selectedDifficulty) {
      filtered = filtered.filter(p => p.difficulty === selectedDifficulty);
    }

    // Filter by company
    if (selectedCompany) {
      filtered = filtered.filter(p =>
        p.companies && p.companies.some(c => c.name === selectedCompany)
      );
    }

    // Filter by pattern
    if (selectedPattern) {
      filtered = filtered.filter(p =>
        p.pattern && p.pattern.name === selectedPattern
      );
    }

    // Filter by status
    if (selectedStatus) {
      if (selectedStatus === "checked") {
        filtered = filtered.filter(p => completionStatus[p.id] === true);
      } else if (selectedStatus === "unchecked") {
        filtered = filtered.filter(p => completionStatus[p.id] !== true);
      }
    }

    setProblems(filtered);
  };

  // Handle filter changes
  useEffect(() => {
    const filtered = searchQuery
      ? (collectionProblems || []).filter(p =>
          p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      : collectionProblems || [];

    applyFilters(filtered);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDifficulty, selectedCompany, selectedPattern, selectedStatus, collectionProblems, completionStatus]);

  const handleClearFilters = () => {
    setSelectedDifficulty("");
    setSelectedCompany("");
    setSelectedPattern("");
    setSelectedStatus("");
  };



  const handleAddProblems = async (leetcodeNumbers: number[], generateMissing: boolean) => {
    if (!collection) return;
    
    try {
      setIsGenerating(true);
      
      // Use batch API to fetch all problems at once
      console.log('Calling batch API with numbers:', leetcodeNumbers);
      const batchResult = await apiService.getProblemsByLeetcodeNumbers(leetcodeNumbers);
      console.log('Batch API result:', batchResult);
      
      if (!batchResult.success) {
        console.error('Batch API failed:', batchResult);
        throw new Error(batchResult.message || 'Failed to fetch problems');
      }
      
      // Add found problems to collection
      const addedProblems: number[] = [];
      const failedProblems: number[] = [];
      
      for (const leetcodeNumber of batchResult.found) {
        const problem = batchResult.data.get(leetcodeNumber);
        if (problem && problem.id) {
          try {
            await collectionsApi.addProblemToCollection(collection.id, problem.id);
            addedProblems.push(leetcodeNumber);
          } catch (error) {
            console.error(`Failed to add problem #${leetcodeNumber} to collection:`, error);
            failedProblems.push(leetcodeNumber);
            // Continue with next problem even if one fails
          }
        }
      }
      
      // Handle missing problems with generation if requested
      if (batchResult.notFound.length > 0) {
        if (generateMissing) {
          console.log(`Generating ${batchResult.notFound.length} missing problems with AI`);
          
          // Initialize progress tracking
          const batchSize = 5; // Matches backend configuration
          const totalBatches = Math.ceil(batchResult.notFound.length / batchSize);
          
          setGenerationProgress({
            totalCount: batchResult.notFound.length,
            currentBatch: 1,
            totalBatches,
            generatedCount: 0,
            failedCount: 0,
            isComplete: false,
            message: `Generating ${batchResult.notFound.length} problems...`
          });
          setShowGenerationProgress(true);
          setIsGenerating(false); // Hide the simple loading modal
          
          try {
            // Generate missing problems
            const generationResult = await apiService.generateProblemsByLeetcodeNumbers(batchResult.notFound);
            
            // Update progress with results
            const generatedCount = Object.keys(generationResult.generated).length;
            const failedCount = generationResult.failed.length;
            
            setGenerationProgress(prev => ({
              ...prev,
              generatedCount,
              failedCount,
              isComplete: true,
              message: generationResult.message
            }));
            
            // Add generated problems to collection
            for (const [leetcodeNumber, problem] of Object.entries(generationResult.generated)) {
              try {
                await collectionsApi.addProblemToCollection(collection.id, problem.id);
                addedProblems.push(parseInt(leetcodeNumber));
              } catch (error) {
                console.error(`Failed to add generated problem #${leetcodeNumber} to collection:`, error);
                failedProblems.push(parseInt(leetcodeNumber));
              }
            }
            
            // Wait a moment to show completion
            setTimeout(() => {
              setShowGenerationProgress(false);
            }, 2000);
            
          } catch (error) {
            console.error('Failed to generate problems:', error);
            setGenerationProgress(prev => ({
              ...prev,
              isComplete: true,
              message: 'Generation failed. Please try again.'
            }));
            setTimeout(() => {
              setShowGenerationProgress(false);
            }, 3000);
          }
        } else {
          console.warn(`The following problems were not found in the database: ${batchResult.notFound.join(', ')}`);
          console.warn('Generation was not requested.');
        }
      }
      
      // Log summary
      console.log(`Successfully added ${addedProblems.length} problems to collection`);
      if (failedProblems.length > 0) {
        console.warn(`Failed to add ${failedProblems.length} problems: ${failedProblems.join(', ')}`);
      }
      
      // Reload collection to show new problems
      await loadCollection();
    } catch (error) {
      console.error('Failed to add problems:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRemoveProblem = (problemId: string) => {
    setProblemToRemove(problemId);
    setShowRemoveModal(true);
  };

  const confirmRemoveProblem = async () => {
    if (!collection || !problemToRemove) return;
    
    setShowRemoveModal(false);
    setIsDeleting(true);
    
    try {
      await collectionsApi.removeProblemFromCollection(collection.id, problemToRemove);
      setIsDeleting(false);
      await loadCollection(true); // Show refreshing state
    } catch (error) {
      console.error('Failed to remove problem:', error);
      setIsDeleting(false);
    } finally {
      setProblemToRemove(null);
    }
  };

  const cancelRemoveProblem = () => {
    setShowRemoveModal(false);
    setProblemToRemove(null);
  };

  // const handleGenerateProblem = async () => {
  //   // Show modal to generate problem
  //   console.log('Generate problem modal would open here');
  // };

  const handleUpdateName = async () => {
    if (!collection || editedName.trim() === collection.name) {
      setIsEditingName(false);
      return;
    }
    
    try {
      await collectionsApi.updateCollection(collection.id, { name: editedName.trim() });
      setCollection({ ...collection, name: editedName.trim() });
      setIsEditingName(false);
    } catch (error) {
      console.error('Failed to update name:', error);
    }
  };

  const handleUpdateDesc = async () => {
    if (!collection || editedDesc.trim() === collection.description) {
      setIsEditingDesc(false);
      return;
    }

    try {
      await collectionsApi.updateCollection(collection.id, { description: editedDesc.trim() });
      setCollection({ ...collection, description: editedDesc.trim() });
      setIsEditingDesc(false);
    } catch (error) {
      console.error('Failed to update description:', error);
    }
  };

  const handleTogglePinProblem = async (problemId: string) => {
    if (!collection?.id) return;

    const isPinned = isProblemPinned(collection.id, problemId);

    if (isPinned) {
      const result = await unpinProblem(collection.id, problemId);
      if (!result.success && result.message) {
        alert(result.message);
      }
    } else {
      // Check if at max pins for this collection
      const collectionPins = pinData.pinnedProblems[collection.id] || [];
      if (collectionPins.length >= maxPinnedProblemsPerCollection) {
        alert(`You can only pin up to ${maxPinnedProblemsPerCollection} problems per collection. Please unpin one before pinning another.`);
        return;
      }

      const result = await pinProblem(collection.id, problemId);
      if (!result.success && result.message) {
        alert(result.message);
      }
    }
  };

  // Apply pin sorting to problems
  const sortedProblems = collection?.id ? sortProblems(collection.id, problems || []) : (problems || []);
  const displayProblems = sortedProblems.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(sortedProblems.length / pageSize);

  const getCompletedCount = () => {
    if (!completionStatus || Object.keys(completionStatus).length === 0) {
      return 0;
    }
    return (collectionProblems || []).filter(p => p.id && completionStatus[p.id]).length;
  };

  // Check if all problems are completed
  const allCompleted = collectionProblems.length > 0 &&
    collectionProblems.every(p => p.id && completionStatus[p.id]);

  // Handler for Mark All Completed
  const handleMarkAllCompleted = async () => {
    if (!userProfile || collectionProblems.length === 0) return;

    const problemIds = collectionProblems.map(p => p.id).filter(Boolean);
    const targetState = !allCompleted; // If all completed, mark as uncompleted; otherwise mark as completed

    setIsMarkingAll(true);
    setShowMarkAllModal(false);

    try {
      await apiService.batchUpdateCompletionStatus(problemIds, targetState);

      // Update local state
      const newCompletionStatus = { ...completionStatus };
      problemIds.forEach(id => {
        newCompletionStatus[id] = targetState;
      });
      setCompletionStatus(newCompletionStatus);

      logger.info(`Successfully marked ${problemIds.length} problems as ${targetState ? 'completed' : 'uncompleted'}`);
    } catch (error) {
      logger.error('Failed to mark all problems:', error);
      alert('Failed to update completion status. Please try again.');
    } finally {
      setIsMarkingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="collection-page">
        <LoadingModal
          isOpen={true}
          message="Loading collection and problems..."
        />
      </div>
    );
  }

  if (!collection) {
    return <div className="error-container">Collection not found</div>;
  }

  return (
    <ActiveProblemProvider>
    <div className="collection-page">
      <div className="collection-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Collections
        </button>
        
        <div className="collection-info">
          {isEditingName ? (
            <div className="edit-name">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleUpdateName}
                onKeyPress={(e) => e.key === 'Enter' && handleUpdateName()}
                autoFocus
                className="name-input"
              />
            </div>
          ) : (
            <h1 className="collection-name" onClick={() => userProfile && collection.ownerId === userProfile.uid && setIsEditingName(true)}>
              {collection.name}
              {userProfile && collection.ownerId === userProfile.uid && <span className="edit-icon">✏️</span>}
            </h1>
          )}
          
          {isEditingDesc ? (
            <div className="edit-desc">
              <textarea
                value={editedDesc}
                onChange={(e) => setEditedDesc(e.target.value)}
                onBlur={handleUpdateDesc}
                rows={2}
                className="desc-input"
              />
            </div>
          ) : (
            <p className="collection-desc" onClick={() => userProfile && collection.ownerId === userProfile.uid && setIsEditingDesc(true)}>
              {collection.description}
              {userProfile && collection.ownerId === userProfile.uid && <span className="edit-icon">✏️</span>}
            </p>
          )}
          
          <div className="collection-stats">
            <span>{collection.stats?.totalProblems || (collection as any).totalProblems || 0} problems</span>
            <span className="separator">•</span>
            <span>{getCompletedCount()} completed</span>
            <span className="separator">•</span>
            <span>Last updated {formatFirestoreDate(collection.updatedAt)}</span>
          </div>
          
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="action-buttons-row">
        {/* Add More button - only visible to authenticated collection owners */}
        {collection && userProfile && collection.ownerId === userProfile.uid && (
          <button
            className="btn-add-more"
            onClick={() => setShowAddProblemsModal(true)}
          >
            <span className="plus-icon">+</span>
            <span>Add More</span>
          </button>
        )}

        {/* Manage Sequence button - visible to collection owners */}
        {collection && userProfile && collection.ownerId === userProfile.uid && (
          <button
            className="btn-manage-sequence"
            onClick={() => navigate(`/collections/${id}/sequence`)}
          >
            <span className="sequence-icon">↕</span>
            <span>Manage Sequence</span>
          </button>
        )}

        {/* Mark All Completed button - visible to authenticated users */}
        {userProfile && collectionProblems.length > 0 && (
          <button
            className={`btn-mark-all ${allCompleted ? 'all-completed' : ''}`}
            onClick={() => setShowMarkAllModal(true)}
            disabled={isMarkingAll}
            title={allCompleted ? 'Unmark all problems as completed' : 'Mark all problems as completed'}
          >
            <span className="checkmark-icon">{allCompleted ? '☑' : '☐'}</span>
            <span>{isMarkingAll ? 'Updating...' : (allCompleted ? 'Unmark All' : 'Mark All Completed')}</span>
          </button>
        )}
      </div>

      {/* Filters Row */}
      <div className="filters-row">
        <CollectionFilters
          selectedDifficulty={selectedDifficulty}
          onDifficultyChange={setSelectedDifficulty}
          selectedCompany={selectedCompany}
          onCompanyChange={setSelectedCompany}
          selectedPattern={selectedPattern}
          onPatternChange={setSelectedPattern}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          availableCompanies={getAvailableCompanies()}
          availablePatterns={getAvailablePatterns()}
          onClearFilters={handleClearFilters}
        />
      </div>

      <div className="search-section">
        <CollectionSearchBar
          onSearch={handleSearch}
          isLoading={loading}
        />
      </div>


      <div className="problems-section">
        {displayProblems.length === 0 ? (
          <div className="empty-problems">
            {searchQuery ? (
              <p>No problems found matching "{searchQuery}"</p>
            ) : (collectionProblems || []).length === 0 ? (
              <>
                <p>This collection is empty.</p>
                {userProfile && collection && collection.ownerId === userProfile.uid && (
                  <button 
                    className="add-first-btn"
                    onClick={() => setShowAddProblemsModal(true)}
                  >
                    Add Your First Problem
                  </button>
                )}
              </>
            ) : (
              <p>No problems to display</p>
            )}
          </div>
        ) : (
          <>
            {displayProblems.map((problem, index) => {
              const isPinned = collection?.id ? isProblemPinned(collection.id, problem.id) : false;
              const canManage = collection && userProfile && collection.ownerId && collection.ownerId === userProfile.uid;

              return (
                <div key={problem.id} id={`problem-${problem.id}`} className="collection-problem-wrapper">
                  <ProblemCard
                    problem={problem}
                    index={(currentPage - 1) * pageSize + index}
                    showTooltips={index === 0} // Only show first-time tooltips on the first card
                    onProblemUpdate={(updatedProblem) => {
                      // Update the problem in the local state
                      const updatedProblems = problems.map(p =>
                        p.id === updatedProblem.id ? updatedProblem : p
                      );
                      setProblems(updatedProblems);
                      const updatedCollectionProblems = collectionProblems.map(p =>
                        p.id === updatedProblem.id ? updatedProblem : p
                      );
                      setCollectionProblems(updatedCollectionProblems);
                    }}
                    customHeaderAction={
                      // Show pin and remove buttons for user's own collections
                      canManage ? (
                        <div className="problem-header-actions">
                          <PinIcon
                            isPinned={isPinned}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePinProblem(problem.id);
                            }}
                            size={18}
                          />
                          <button
                            className="collection-remove-btn"
                            onClick={() => handleRemoveProblem(problem.id)}
                            title="Remove from collection"
                            disabled={isDeleting}
                          >
                            <TrashIcon size={18} />
                          </button>
                        </div>
                      ) : null
                    }
                  />
                </div>
              );
            })}
          </>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNext={currentPage < totalPages}
          hasPrev={currentPage > 1}
          onPageChange={setCurrentPage}
        />
      )}

      <ConfirmationModal
        isOpen={showRemoveModal}
        title="Remove Problem"
        message="Are you sure you want to remove this problem from the collection?"
        onConfirm={confirmRemoveProblem}
        onCancel={cancelRemoveProblem}
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Mark All Completed Confirmation Modal */}
      <ConfirmationModal
        isOpen={showMarkAllModal}
        title={allCompleted ? "Unmark All Problems?" : "Mark All as Completed?"}
        message={allCompleted
          ? `This will mark ${collectionProblems.length} problems as not completed.`
          : `This will mark ${collectionProblems.length - getCompletedCount()} remaining problems as completed.`
        }
        onConfirm={handleMarkAllCompleted}
        onCancel={() => setShowMarkAllModal(false)}
        confirmText={allCompleted ? "Unmark All" : "Mark All Completed"}
        cancelText="Cancel"
      />

      <LoadingModal
        isOpen={isMarkingAll}
        message={`Updating ${collectionProblems.length} problems...`}
      />

      <LoadingModal
        isOpen={isDeleting}
        message="Removing problem from collection..."
      />
      
      <LoadingModal
        isOpen={isRefreshing}
        message="Refreshing collection..."
      />
      
      <LoadingModal
        isOpen={isSearching}
        message="Searching problems..."
      />
      
      <LoadingModal
        isOpen={isGenerating}
        message="AI is generating the problem..."
      />
      
      <LoadingModal
        isOpen={isAddingToCollection}
        message="Adding problem to collection..."
      />
      
      <AddProblemsModal
        isOpen={showAddProblemsModal}
        onClose={() => setShowAddProblemsModal(false)}
        onAdd={handleAddProblems}
        collectionName={collection?.name || ''}
      />
      
      {showGenerationProgress && (
        <GenerationProgress
          totalCount={generationProgress.totalCount}
          currentBatch={generationProgress.currentBatch}
          totalBatches={generationProgress.totalBatches}
          generatedCount={generationProgress.generatedCount}
          failedCount={generationProgress.failedCount}
          isComplete={generationProgress.isComplete}
          message={generationProgress.message}
        />
      )}

      <FloatingChatButton />
    </div>
    </ActiveProblemProvider>
  );
};